import { findSlot } from '../ad-services/ad-services.utils';
import {
    AdServers_Supported_Prebid,
    NATIVE_BIDDERS,
    NATIVE_BIDDERS_PARAMS_NAME,
    NATIVE_BIDDERS_WITH_PARAMS,
    NATIVE_PARAMS,
    NATIVE_SGP,
    nativeObject,
    NOT_SUPPORTED_IX_SIZES,
} from './config';
import { IBidder, IConfig, IDetection, InnerSizes, IPrebidsConfig, IPrebidsPriority, ISlot, MappingType, SizeType } from '../../uanc.types';
import { IBidResponses, IPbjsAdUnit, IPbjsBid } from './prebid.types';
import { ServiceSlotType, ServiceType } from '../ad-services/ad-services.types';
import { AD_SERVERS } from '../../configs/constants';
import { IRenderedPlacementDFP } from '../ad-services/DFP/service-dfp.types';

/**
 * Zwraca nazwy dostawców w przekazanej tablicy.
 *
 * @param {IBidder[]} bidders - tablica bidders
 * @returns {string[]} zwraca nazwy dostawców
 */
const getBiddersCodes = (bidders: IBidder[]): string[] => {
    return bidders.map(({ name }) => name);
};

/**
 * Zwraca priorytet konkretnego biddera.
 *
 * @param {string} bidderName - nazwa biddera
 * @param {IPrebidsPriority[]} prebidsPriorityConfig - głowne ustawienia priorytetów dla prebida
 * @returns {number} zwraca priorytet dla biddera
 */
export const getBidderPriority = (bidderName: string, prebidsPriorityConfig: IPrebidsPriority[]): number => {
    const bidderPriority = prebidsPriorityConfig.find((priorityConfig) => priorityConfig.value === bidderName);
    return bidderPriority ? bidderPriority.priority : 0;
};

/**
 * Przygotowuje konfiguracje dostawców (adapterów), którzy mają brać udział w licytacji. Ustawia wymagane parametry.
 *
 * @param {IBidder[]} slotBidders - nazwa biddera
 * @returns { IPbjsBid[] } zwraca przygotowane obiekty bidderów
 */
export const prepareBids = (slotBidders: IBidder[]): IPbjsBid[] => {
    const { detection, config } = window.Inpl.Ad;
    const { browserWidth, isMobile } = detection as IDetection;
    const prebidsConfig = config?.prebidsConfig as IPrebidsConfig;

    const bids = slotBidders.reduce((bids: IPbjsBid[], { name, paramSets }) => {
        if ((prebidsConfig.disabledBidderNames || []).includes(name)) return bids;
        return [
            ...bids,
            ...(paramSets || [])
                .filter(({ mobile = true, desktop = true }) => (mobile && isMobile) || (desktop && !isMobile))
                .filter(({ minWidth }) => !minWidth || minWidth <= browserWidth)
                .filter(({ maxWidth }) => !maxWidth || maxWidth > browserWidth)
                .map(({ params }) => {
                    const bidConfig = {
                        bidder: name,
                        params: params.reduce((params, { name, value }) => {
                            return {
                                ...params,
                                [name]: value,
                            };
                        }, {}),
                        priority: prebidsConfig.enabledOrder ? getBidderPriority(name, prebidsConfig.priority) : 0,
                    };
                    if (name === 'rtbhouse') {
                        bidConfig.params = {
                            ...bidConfig.params,
                            region: 'prebid-eu',
                            publisherId: 'oScqvFoFcqVeQET3ISu3',
                        };
                    }
                    return bidConfig;
                }),
        ];
    }, []);

    if (prebidsConfig.enabledOrder) {
        bids.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    }

    return bids;
};

/**
 * Sprawdza ustawienia czy licytacje i Prebid może być wykorzystywany.
 *
 * @returns {boolean}
 */
export const isPrebidAccepted = (): boolean => {
    const { config, plugins, state } = window.Inpl.Ad;
    return !!state?.isPrebidReady && !!config?.prebidsConfig.enabled && (plugins?.RodoKeyword?.keywords as string[])[0] !== '2';
};

/**
 * Zwraca konfiguracje slotów dla wskazancyh jednostek reklamowych.
 * Przekazuje sloty wyłącznie z adserwerem obsługującym prebid, z właczoną obsługą prebida i ustawionymi bidderami.
 * Uwzględnia sloty natywne SGP (NATIVE_SGP).
 *
 * @param {string[]} slotNames - nazwy slotów
 * @param {string[]} isIncludeNativeSGP - czy uwzględnić ustawienia bidderów dla NATIVE_SGP
 * @returns {ISlot[]} obiekty slotów
 */
export const getSlotConfigWithBidders = (slotNames: string[], isIncludeNativeSGP: boolean): ISlot[] => {
    const config = window.Inpl.Ad.config as IConfig;
    const disabledBidderNames = config.prebidsConfig.disabledBidderNames || [];

    return slotNames.reduce((slots, currentSlotName) => {
        const slotConfig = findSlot<ISlot>(currentSlotName, config.slots);
        if (!slotConfig) return slots;

        const isValidSlot: boolean = !!(
            AdServers_Supported_Prebid.includes(slotConfig.adServer) &&
            slotConfig.prebid &&
            slotConfig.bidders?.length
        );

        if (slotConfig.bidders?.length === 1 && disabledBidderNames.includes(slotConfig.bidders[0].name)) return slots;

        if (isValidSlot || (isIncludeNativeSGP && slotConfig.name in NATIVE_SGP)) {
            return [...slots, slotConfig];
        }

        return slots;
    }, [] as ISlot[]);
};

/**
 * Zwraca nazwy bidderów ustawionych dla slotu (uwzględnia sloty SGP NATIVE).
 * Na podstawie zwróconych nazw bidderów ładowane będą, np. adaptery do ich obsługi w prebid.
 * Pomijane wyłączone biddery.
 *
 * @param {ISlot[]} slots - konfiguracje slotów
 * @param {string[]} isIncludeNativeSGP - czy uwzględnić biddery dla NATIVE_SGP
 * @returns { string[] } nazwy bidderów
 */
export const getSlotBidderNames = (slots: ISlot[], isIncludeNativeSGP: boolean): string[] => {
    const { prebidsConfig } = window.Inpl.Ad.config as IConfig;
    const disabledBidderNames = prebidsConfig.disabledBidderNames || [];

    const bidderNames = slots.reduce((names: string[], slotConfig: ISlot) => {
        if (isIncludeNativeSGP && slotConfig.name in NATIVE_SGP) {
            return [...names, ...NATIVE_SGP[slotConfig.name].map(({ bidder }) => bidder)];
        }
        return [...names, ...getBiddersCodes(slotConfig.bidders || [])];
    }, []);

    return disabledBidderNames.length
        ? [...new Set(bidderNames)].filter((bidderName) => !disabledBidderNames.includes(bidderName))
        : [...new Set(bidderNames)];
};

/**
 * Zwraca dopasowane rozmiary reklam w zależności od przekazanego mappingu.
 *
 * @param {MappingType[]} slotMapping - mapping slotu
 * @returns {SizeType[]} rozmiary
 */
export const getSizes = (slotMapping: MappingType[]): SizeType[] => {
    const foundMapping: MappingType | undefined = slotMapping.find(
        /* eslint-disable @typescript-eslint/no-unused-vars */
        ([SizeType, InnerSizes]) => SizeType[0] < innerWidth && SizeType[1] < innerHeight
    );
    const matchingMapping: InnerSizes = foundMapping ? foundMapping[1] : [];

    return matchingMapping.filter(
        (size) => Array.isArray(size) && typeof size[0] === 'number' && typeof size[1] === 'number' && size[0] !== 1 && size[1] !== 1
    ) as SizeType[];
};

/**
 * Zwraca dopasowane wymiary reklamy dla dostawcy IX.
 *
 * @param {SizeType[]} sizes - wymiary reklam
 * @returns {SizeType[]} rozmiary
 */
export const getSizesIX = (sizes: SizeType[]) => {
    return sizes.filter(([width, height]) => {
        const isSizeNotSupportedByIX = !!NOT_SUPPORTED_IX_SIZES.find(
            ([notSupportedWidth, notSupportedHeight]) => width === notSupportedWidth && height === notSupportedHeight
        );
        return !isSizeNotSupportedByIX;
    });
};

/**
 * Sloty NATIVE_SGP tylko dla SGP
 * Funkcja tworzy jednostki reklamowe (Prebid) dla zdefiniowanych jednostek (GPT). Uwzględnia zdefiniowane jednostki
 *
 * @param {string} slotName - nzawa slotu
 * @param {string[]} adUnitNames - nzawy zdefiniowanych jednostek reklamowych / placeholderId
 * @returns { IPbjsAdUnit[]} konfiguracja jednostek reklamowych - prebid
 */
export const createSGPNativePrebidAdUnit = (slotName: string, adUnitNames: string[]): IPbjsAdUnit[] => {
    const { config } = window.Inpl.Ad;
    const prebidsConfig = config?.prebidsConfig as IPrebidsConfig;
    const adUnits: IPbjsAdUnit[] = [];

    (NATIVE_SGP[slotName] || []).forEach((item: IPbjsBid) => {
        if ((prebidsConfig.disabledBidderNames || []).includes(item.bidder)) return;

        const nativeBidder: IPbjsBid = {
            bidder: item.bidder,
            params: item.params,
            priority: prebidsConfig.enabledOrder ? getBidderPriority(item.bidder, prebidsConfig.priority) : 0,
        };

        adUnitNames.forEach((adUnitName) => {
            adUnits.push({
                code: adUnitName,
                mediaTypes: {
                    native: nativeObject,
                },
                bids: [nativeBidder],
            });
        });
    });

    return adUnits;
};

/**
 * Native sloty dla konkretnych ścieżek - NATIVE_SERVICES
 * Tworzy jednostki reklamowe (Prebid) dla wskazanych jednostek (GPT). Osobne jednostki w zależności od typów reklam i dostawców.
 *
 * @param {string} slotName - nzawa slotu
 * @param {string[]} adUnitNames - nzawy zdefiniowanych jednostek reklamowych / placeholderId
 * @param {IPbjsBid[]} bids - obikety z ustawieniami dostawców
 * @param {SizeType[]} sizes -  rozmiary reklam
 * @param {SizeType[]} sizesIX -  rozmiary reklam dla IX
 * @returns { IPbjsAdUnit[] } ustawione jednostki reklamowe - prebid Ad units
 */
export const createNativePrebidAdUnit = (
    slotName: string,
    adUnitNames: string[],
    bids: IPbjsBid[],
    sizes: SizeType[],
    sizesIX: SizeType[]
): IPbjsAdUnit[] => {
    const adUnits: IPbjsAdUnit[] = [];

    // jednostki z typami reklam: banner + native, dostwcy obsługujący native
    adUnitNames.forEach((adUnitName) => {
        adUnits.push({
            code: adUnitName,
            mediaTypes: {
                banner: {
                    sizes: sizes,
                },
                native: nativeObject,
            },
            bids: bids.filter((item) => NATIVE_BIDDERS.includes(item.bidder)),
        });
    });

    // jednostki z typem reklamy: banner, bez biddera IX i obsługujących native
    adUnitNames.forEach((adUnitName) => {
        adUnits.push({
            code: adUnitName,
            mediaTypes: {
                banner: {
                    sizes: sizes,
                },
            },
            bids: bids.filter((item) => !NATIVE_BIDDERS.includes(item.bidder) && item.bidder !== 'ix'),
        });
    });

    // jednostki z typem reklamy: banner, wyłącznie bidder IX
    const bidsIX = bids.filter((item) => item.bidder === 'ix');
    if (bidsIX.length && sizesIX)
        adUnitNames.forEach((adUnitName) => {
            adUnits.push({
                code: adUnitName,
                mediaTypes: {
                    banner: {
                        sizes: sizesIX,
                    },
                },
                bids: bidsIX,
            });
        });

    // specjalne paramsy dla appnexus i ix
    NATIVE_BIDDERS_WITH_PARAMS.forEach((item) => {
        const bidder = bids.filter((bid) => bid.bidder === item);
        if (item in NATIVE_PARAMS && slotName in NATIVE_PARAMS[item]) {
            const nativeBidder = {
                ...bidder[0],
                params: {
                    [NATIVE_BIDDERS_PARAMS_NAME[item]]: NATIVE_PARAMS[item][slotName],
                },
            };

            adUnitNames.forEach((adUnitName) => {
                adUnits.push({
                    code: adUnitName,
                    mediaTypes: {
                        native: nativeObject,
                    },
                    bids: [nativeBidder],
                });
            });
        }
    });

    return adUnits;
};

/**
 * Tworzy jednostki reklamowe (Prebid) dla wskazanych jednostek (GPT). Osobne jednostki do IX.
 *
 * @param {string[]} adUnitNames - nzawy zdefiniowanych jednostek reklamowych / placeholderId
 * @param {IPbjsBid[]} bids - obikety z ustawieniami dostawców
 * @param {SizeType[]} sizes -  rozmiary reklam
 * @param {SizeType[]} sizesIX -  rozmiary reklam dla IX
 * @returns { IPbjsAdUnit[] } ustawione jednostki reklamowe - prebid Ad units
 */
export const createPrebidAdUnit = (adUnitNames: string[], bids: IPbjsBid[], sizes: SizeType[], sizesIX: SizeType[]): IPbjsAdUnit[] => {
    const adUnits: IPbjsAdUnit[] = [];

    const bidsNoIX = bids.filter((item) => item.bidder !== 'ix');
    const bidsIX = bids.filter((item) => item.bidder === 'ix');
    if (sizes.length && bidsNoIX.length) {
        adUnitNames.forEach((adUnitName) => {
            adUnits.push({
                code: adUnitName,
                mediaTypes: {
                    banner: {
                        sizes: sizes,
                    },
                },
                bids: bidsNoIX,
            });
        });
    }
    if (sizesIX.length && bidsIX.length) {
        adUnitNames.forEach((adUnitName) => {
            adUnits.push({
                code: adUnitName,
                mediaTypes: {
                    banner: {
                        sizes: sizesIX,
                    },
                },
                bids: bidsIX,
            });
        });
    }

    return adUnits;
};

/**
 * Bazując na odpowiedzi z prebida 'requestBids', umieszcza w konfiguracji slotu informacje o zakończonej licytacji.
 *
 * @param {IBidResponses} bidResponses - obiekt odpowiedzi zapytania aukcyjnego
 * @param {ISlot[]} slotsToUpdate - sloty przekazane do licytacji
 * @returns { void }
 */
export const updateBidInfo = (bidResponses: IBidResponses, slotsToUpdate: ISlot[]): void => {
    const { services } = window.Inpl.Ad;

    Object.entries(bidResponses).forEach(([adUnitName, { bids }]) => {
        slotsToUpdate.forEach((slot) => {
            const service = services?.[`${slot.adServer}Service`] as ServiceType;
            // konfiguracja slotu z odpowiedniego serwisu
            const serviceSlot = (service.slots as ServiceSlotType[]).find((slot) => slot.placementIds.includes(adUnitName));
            if (slot.adServer === AD_SERVERS.DFP && serviceSlot) {
                serviceSlot['renderedPlacements'] = {
                    ...(serviceSlot['renderedPlacements'] ?? {}),
                    [adUnitName]: {
                        bids,
                    } as IRenderedPlacementDFP,
                };
            }
        });
    });
};

/**
 * W przypadku braku odpowiedzi o wygranej kreacji z prebida - czyszczenie informacji o poprzednich licytacjach.
 *
 * @param {IPbjsAdUnit[]} pbAdUnits - jednostki reklamowe - prebid Ad units, o które wysłane zostały zapytania
 * @param {ISlot[]} slotsToUpdate - sloty przekazane do licytacji
 * @returns { void }
 */
export const cleanBidInfo = (pbAdUnits: IPbjsAdUnit[], slotsToUpdate: ISlot[]): void => {
    const { services } = window.Inpl.Ad;

    const adUnitNames: string[] = Array.from(new Set(pbAdUnits.map((pbAdUnit) => pbAdUnit.code)));

    adUnitNames.forEach((adUnitName) => {
        slotsToUpdate.forEach((slot) => {
            const service = services?.[`${slot.adServer}Service`] as ServiceType;
            // konfiguracja slotu z odpowiedniego serwisu
            const serviceSlot = (service.slots as ServiceSlotType[]).find(
                (slot) => slot.placementIds.includes(adUnitName) && slot.renderedPlacements?.[adUnitName]
            );

            if (slot.adServer === AD_SERVERS.DFP && serviceSlot?.renderedPlacements?.[adUnitName]) {
                (serviceSlot.renderedPlacements[adUnitName] as IRenderedPlacementDFP).bids = null;
            }
        });
    });
};
